import React, { useEffect } from 'react';
import PageLayout from '../components/templates/PageLayout';
import LinkButton from '../components/atoms/LinkButton';
import Testimonial from '../components/molecules/Testimonial';
import YoutubeEmbed from '../components/molecules/YoutubeEmbed';
import { activeOffer } from '../data/activeOffer';
import { getTestimonialsByIds } from '../data/testimonials';

const campParentTestimonial = getTestimonialsByIds(['teddy-dad'])[0];

const Camp = () => {
  const { archive } = activeOffer;

  useEffect(() => {
    document.title = 'Camps | DataRook';
  }, []);

  return (
    <PageLayout>
      <div className="dr-container py-16">
        <div className="text-center mb-8">
          <h1 className="text-5xl font-black mb-4 text-navy">DataRook Camps</h1>
          <p className="text-xl text-gray-600">Where Soccer Meets Data Science!</p>
        </div>

        <div className="max-w-5xl mx-auto mb-16">
          <div className="bg-gradient-to-r from-navy via-blue-800 to-navy p-8 md:p-12 rounded-xl shadow-2xl">
            <div className="text-center mb-8">
              <p className="inline-block bg-white/20 text-yellow font-bold text-sm uppercase tracking-wide px-4 py-1 rounded-full mb-4">
                Past camp · July 2026
              </p>
              <div className="mb-4">
                <span className="text-4xl">⚽</span>
                <span className="text-4xl mx-2">🏆</span>
                <span className="text-4xl">⚽</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-black mb-4 text-yellow">{archive.name}</h2>
              <p className="text-lg text-white">
                <strong>{archive.dates}</strong> (Wed-Fri) · {archive.location}
              </p>
              <p className="text-base text-white/90 mt-2 max-w-2xl mx-auto">{archive.thankYou}</p>
            </div>

            <p className="text-center text-white/80 text-sm uppercase tracking-wide mb-4">
              Details from this past camp
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white/10 rounded-lg p-6">
                <h3 className="text-xl font-bold text-yellow mb-4">When & Where</h3>
                <div className="space-y-3 text-white">
                  <p className="flex items-start">
                    <span className="text-yellow mr-2">📅</span>
                    <span>
                      <strong>{archive.dates}</strong>
                      <br />
                      Wednesday through Friday (3 days)
                    </span>
                  </p>
                  <p className="flex items-start">
                    <span className="text-yellow mr-2">⏰</span>
                    <span>
                      <strong>8:00 AM - 12:00 PM daily</strong>
                      <br />
                      Half-day camp
                    </span>
                  </p>
                  <p className="flex items-start">
                    <span className="text-yellow mr-2">📍</span>
                    <span>
                      <strong>Sacred Heart</strong>
                      <br />
                      417 Erin Dr, Knoxville, TN 37919
                    </span>
                  </p>
                </div>
              </div>

              <div className="bg-white/10 rounded-lg p-6">
                <h3 className="text-xl font-bold text-yellow mb-4">Who & What</h3>
                <div className="space-y-3 text-white">
                  <p className="flex items-start">
                    <span className="text-yellow mr-2">👦👧</span>
                    <span>
                      <strong>Rising 6th, 7th & 8th graders</strong>
                      <br />
                      All skill levels, no experience needed
                    </span>
                  </p>
                  <p className="flex items-start">
                    <span className="text-yellow mr-2">🎯</span>
                    <span>
                      <strong>Small groups</strong>
                      <br />
                      Hands-on soccer and data science
                    </span>
                  </p>
                  <p className="flex items-start">
                    <span className="text-yellow mr-2">🍎</span>
                    <span>
                      <strong>Daily snacks provided</strong>
                      <br />
                      Fruit + treats to keep campers fueled
                    </span>
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 rounded-lg p-6 mb-8">
              <h3 className="text-xl font-bold text-yellow mb-4 text-center">
                What Made This Camp Special
              </h3>
              <div className="text-white space-y-4 max-w-3xl mx-auto">
                <p>
                  This wasn&apos;t just a soccer camp—it was a{' '}
                  <strong>full World Cup experience</strong>. Each camper represented a real World
                  Cup nation all week long, competing on the field in matches while also diving into
                  professional-level data analysis in the computer lab.
                </p>
                <p>
                  Using <strong>real StatsBomb data</strong> from past Men&apos;s and Women&apos;s
                  World Cups, campers analyzed how the best players in the world perform—then applied
                  those same skills to their own <strong>Catapult GPS tracker</strong> data and built{' '}
                  <strong>Python dashboards</strong> from their matches.
                </p>
                <p>
                  Led by <strong>Gus Alvarez</strong>, DataRook founder and PhD student at the
                  University of Tennessee.
                </p>
                <p className="text-yellow font-semibold text-center">
                  Play like a pro. Analyze like a pro. Think like a pro.
                </p>
              </div>
            </div>

            <div className="text-center">
              <LinkButton
                to={activeOffer.destination}
                variant="primary"
                className="text-xl py-4 px-10 shadow-lg"
              >
                {activeOffer.label}
              </LinkButton>
              <p className="mt-4 text-white/90 max-w-xl mx-auto">
                Interested in future camps or bringing footyLab to your community? Tell us through
                our contact form. {activeOffer.nextStep}
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto mb-16">
          <h2 className="text-3xl font-black mb-8 text-center text-navy">Past Camp Highlights</h2>

          <div className="bg-gray-50 rounded-xl p-8 mb-8">
            <h3 className="text-2xl font-bold mb-4 text-center text-navy">Summer Camp 2025</h3>
            <p className="text-lg text-center mb-8 max-w-3xl mx-auto">
              Our 2025 Summer Camp was an incredible success! Watch the highlights from our week-long
              adventure where middle school students discovered the power of data through soccer.
            </p>

            <YoutubeEmbed
              videoId="6eH8po7p35c"
              title="DataRook Summer Camp 2025 Highlights"
              sectionClassName=""
              className="max-w-4xl mx-auto mb-8"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <div className="bg-yellow p-6 rounded-lg text-center">
                <h4 className="text-4xl font-black text-navy mb-2">20</h4>
                <p className="text-navy font-bold">Happy Campers</p>
              </div>
              <div className="bg-yellow p-6 rounded-lg text-center">
                <h4 className="text-4xl font-black text-navy mb-2">5</h4>
                <p className="text-navy font-bold">Days of Learning & Fun</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto mb-16">
          <h2 className="text-3xl font-black mb-8 text-center text-navy">What Our Camps Offer</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-gray-50 p-8 rounded-lg">
              <h3 className="text-2xl font-bold mb-6 text-navy">Perfect Blend of Sports & STEM</h3>
              <p className="text-lg mb-6">
                Our unique camps combine the excitement of soccer with cutting-edge data science
                education. Campers actively play soccer while learning how professional teams use data
                to improve performance.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-yellow text-xl mr-2">⚽</span>
                  <div>
                    <strong>Play Soccer Daily</strong> - Skills, drills, and scrimmages
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow text-xl mr-2">📊</span>
                  <div>
                    <strong>Analyze Game Data</strong> - Track passes, shots, and movement
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow text-xl mr-2">💻</span>
                  <div>
                    <strong>Learn Data Science</strong> - Visualize and interpret statistics
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow text-xl mr-2">🧠</span>
                  <div>
                    <strong>Problem Solving</strong> - Use data to improve team strategy
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-navy text-white p-8 rounded-lg">
              <h3 className="text-2xl font-bold mb-6 text-yellow">What Campers Learn</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-bold text-yellow mb-2">Soccer Skills</h4>
                  <p>
                    Improve dribbling, passing, shooting, and tactical understanding through guided
                    practice.
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-yellow mb-2">Data Analysis</h4>
                  <p>
                    Learn to collect, organize, and interpret sports performance data using
                    professional tools.
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-yellow mb-2">Technology Skills</h4>
                  <p>
                    Get hands-on experience with data visualization software and basic coding
                    concepts.
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-yellow mb-2">Critical Thinking</h4>
                  <p>Develop problem-solving skills by using data to make strategic decisions.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl font-black mb-8 text-center text-navy">What Parents Say</h2>
          {campParentTestimonial && (
            <Testimonial
              text={campParentTestimonial.text}
              author="Camp Parent, Summer 2025"
              showStars={false}
            />
          )}
        </div>

        <div className="text-center bg-gradient-to-r from-yellow via-yellow to-orange-300 p-12 rounded-lg">
          <h2 className="text-4xl font-black mb-4 text-navy">Want a Camp at Your Location?</h2>
          <p className="text-xl mb-6 text-navy max-w-2xl mx-auto">
            We can bring DataRook camps to schools, community centers, and sports facilities.
            Let&apos;s create an unforgettable learning experience for your students!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <LinkButton
              to={activeOffer.destination}
              variant="partner"
              className="text-xl py-4 px-8"
            >
              {activeOffer.partnerLabel}
            </LinkButton>
            <div className="text-navy">
              <p className="font-semibold">Questions?</p>
              <a
                href={activeOffer.fallbackMailto}
                className="underline hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy"
              >
                info@datarook.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Camp;
